import { useMemo } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useApprovalGroup } from './queries/useApprovalGroup';
import { roleService } from '../services/auth/roleService';
import { RequestStage } from '../types/stages';

/**
 * Approval-group approvers who are not the requestor may Approve/Reject but must not
 * mutate request content (location tags, revision restore, etc.) — UX-DR9 / Story 1.6.
 */
export function useApprovalStampOnlyAccess(params: {
  requestorEmail?: string | null;
  requestStage?: RequestStage | null;
}): {
  viewerIsLiveApprovalGroupMember: boolean;
  viewerIsRequestor: boolean;
  /** Footer/tools on Request Detail while AWAITING_APPROVAL (partner approver, not requestor). */
  viewerHasApprovalStampOnlyAccess: boolean;
  /** Block location tags, revision restore, and similar content mutations for non-requestors. */
  approverMutationsBlocked: boolean;
} {
  const { user } = useAuth();
  const { isImpersonating, targetUser } = useImpersonation();
  const { data: approvalGroup, isLoading: approvalGroupLoading } =
    useApprovalGroup();

  const approvalsEnabled =
    approvalGroup?.enabled === true && (approvalGroup.members?.length ?? 0) > 0;
  const isPartnerViewer = roleService.isExternalUser(user ?? null);
  const approvalGatePending = isPartnerViewer && approvalGroupLoading;

  const viewerIsLiveApprovalGroupMember = useMemo(() => {
    if (!approvalsEnabled || approvalGatePending) {
      return false;
    }
    const uid =
      isImpersonating && targetUser?.user_id != null
        ? targetUser.user_id
        : user?.userId;
    if (uid == null) {
      return false;
    }
    return approvalGroup?.members?.some(m => m.userId === uid) ?? false;
  }, [
    approvalGatePending,
    approvalGroup?.members,
    approvalsEnabled,
    isImpersonating,
    targetUser?.user_id,
    user?.userId,
  ]);

  const viewerEmail = (
    isImpersonating && targetUser?.email ? targetUser.email : user?.email
  )
    ?.trim()
    .toLowerCase();
  const requestorEmailNormalized = params.requestorEmail?.trim().toLowerCase();
  const viewerIsRequestor =
    !!viewerEmail &&
    !!requestorEmailNormalized &&
    viewerEmail === requestorEmailNormalized;

  const requestIsAwaitingApproval =
    params.requestStage === RequestStage.AWAITING_APPROVAL;

  const viewerHasApprovalStampOnlyAccess =
    requestIsAwaitingApproval &&
    isPartnerViewer &&
    viewerIsLiveApprovalGroupMember &&
    !viewerIsRequestor;

  const approverMutationsBlocked =
    requestIsAwaitingApproval &&
    !viewerIsRequestor &&
    (isPartnerViewer || viewerIsLiveApprovalGroupMember);

  return {
    viewerIsLiveApprovalGroupMember,
    viewerIsRequestor,
    viewerHasApprovalStampOnlyAccess,
    approverMutationsBlocked,
  };
}
